<template>
  <v-container fluid class="pa-0">
    <v-row>
      <v-col cols="12" lg="5" xl="4">
        <v-row dense>
          <v-col cols="12" sm="3">
            <v-autocomplete
            label="State"
            autocomplete="disabled"
            id="location-state"
            :items="states"
            @change="stateChanged"
            color="black"
            item-color="primary"
            v-model="countrySubdivision"/>
          </v-col>
          <v-col cols="12" sm="9">
            <v-autocomplete
            autocomplete="disabled"
            id="location-county"
            :items="selectedCounties"
            v-model="countrySecondarySubdivision"
            @change="countyChosen"
            color="black"
            item-color="primary"
            label="County"/>
          </v-col>

          <slot name="map-and-parcel-number"></slot>

          <slot name="acres"></slot>

          <slot name="pickup-radius"></slot>

          <slot name="logging-accessibility"></slot>

          <slot name="scaling-region"></slot>

          <v-col cols="12">
            <DetailCard
            :title="$t('coordinates')">
              <template #title.append>
                <v-row align="center" dense>
                  <v-col>
                    <Icon
                      icon="mdi-compass-outline"
                      iconColor="primary"
                      :small="false"
                      large
                      :tooltipText="$t(latLon ? 'latLonToDMS' : 'dMSToLatLon')"
                      :elementId="latLon ? 'toggle-to-dms' : 'toggle-to-latlon'"
                      @icon-clicked="toggleInputType"
                      margin=""
                    />
                  </v-col>

                  <v-col cols="auto">
                    <v-btn
                    elevation="0"
                    :color="lessVerboseUi ? 'success' : 'primary'"
                    id="location-apply"
                    :disabled="!showApplyButton"
                    @click="applyLocation"
                    :icon="lessVerboseUi"
                    small>
                      <v-icon v-if="lessVerboseUi">
                        mdi-check-circle
                      </v-icon>
                      <span v-else>
                        {{ $t('apply') }}
                      </span>
                    </v-btn>

                    <v-btn
                    :icon="lessVerboseUi"
                    :color="lessVerboseUi ? 'primary' : 'black'"
                    :text="!lessVerboseUi"
                    :outlined="!lessVerboseUi"
                    :class="lessVerboseUi ? 'ml-n2' : 'ml-2'"
                    id="location-reset"
                    :disabled="!showResetButton"
                    @click="resetLocation"
                    small>
                      <v-icon v-if="lessVerboseUi">
                        mdi-arrow-u-left-top-bold
                      </v-icon>
                      <span v-else>
                        {{ $t('reset') }}
                      </span>
                    </v-btn>
                  </v-col>
                </v-row>
              </template>

              <v-row align="center" dense>
                <v-col v-if="latLon" cols="12" sm="6">
                  <v-text-field
                    ref="latTField"
                    v-model="latitudeTextField"
                    id="location-latitude"
                    :label="$t('latitude')"
                    color="black"
                    @blur="onTextFieldBlur"
                    :rules="[rules.required, rules.decimal, rules.validLatitudeForTextBox, rules.sixDecimalPlacesOrFewer]"
                    type="number"
                    hide-spin-buttons
                  />
                </v-col>
                <v-col v-if="latLon" cols="12" sm="6">
                  <v-text-field
                    ref="lonTField"
                    v-model="longitudeTextField"
                    id="location-longitude"
                    :label="$t('longitude')"
                    color="black"
                    @blur="onTextFieldBlur"
                    :rules="[rules.required, rules.decimal, rules.validLongitudeForTextBox, rules.sixDecimalPlacesOrFewer]"
                    type="number"
                    hide-spin-buttons
                  />
                </v-col>

                <v-col v-if="!latLon" cols="12" sm="3">
                  <v-text-field
                    ref="degreesLatTField"
                    v-model="degreesTextFieldLat"
                    id="location-degree-lat"
                    :label="$t('degrees')"
                    color="black"
                    @blur="onTextFieldBlur"
                    :rules="[rules.integer, rules.validDegreesNorthSouthForTextBox]"
                    type="number"
                    hide-spin-buttons
                  />
                </v-col>
                <v-col v-if="!latLon" cols="12" sm="3">
                  <v-text-field
                    ref="minutesLatTField"
                    v-model="minutesTextFieldLat"
                    id="location-minutes-lat"
                    :label="$t('location-minutes')"
                    color="black"
                    @blur="onTextFieldBlur"
                    :rules="[rules.integer, rules.validMinutesForTextBox]"
                    type="number"
                    hide-spin-buttons
                  />
                </v-col>
                <v-col v-if="!latLon">
                  <v-text-field
                    ref="secondsLatTField"
                    v-model="secondsTextFieldLat"
                    id="location-seconds-lat"
                    :label="$t('seconds')"
                    color="black"
                    @blur="onTextFieldBlur"
                    :rules="[rules.required, rules.decimal, rules.fourDecimalPlacesOrFewer, rules.validSecondsForTextBox]"
                    type="number"
                    hide-spin-buttons
                  />
                </v-col>
                <v-col v-if="!latLon" cols="auto">
                  <Icon
                    :icon="degreesNorth ? 'mdi-alpha-n-box-outline' : 'mdi-alpha-s-box-outline'"
                    iconColor="primary"
                    large
                    :small="false"
                    :tooltipText="degreesNorth ? $t('north') : $t('south')"
                    @icon-clicked="toggleNorthSouth"
                  />
                </v-col>
              </v-row>

              <v-row dense align="center" v-if="!latLon">
                <v-col cols="12" sm="3">
                  <v-text-field
                    ref="degreesLonTField"
                    v-model="degreesTextFieldLon"
                    id="location-degree-lon"
                    :label="$t('degrees')"
                    color="black"
                    @blur="onTextFieldBlur"
                    :rules="[rules.integer, rules.validDegreesEastWestForTextBox]"
                    type="number"
                    hide-spin-buttons
                  />
                </v-col>
                <v-col cols="12" sm="3">
                  <v-text-field
                    ref="minutesLonTField"
                    v-model="minutesTextFieldLon"
                    id="location-minutes-lon"
                    :label="$t('location-minutes')"
                    color="black"
                    @blur="onTextFieldBlur"
                    :rules="[rules.integer, rules.validMinutesForTextBox]"
                    type="number"
                    hide-spin-buttons
                  />
                </v-col>
                <v-col>
                  <v-text-field
                    ref="secondsLonTField"
                    v-model="secondsTextFieldLon"
                    id="location-seconds-lon"
                    :label="$t('seconds')"
                    color="black"
                    @blur="onTextFieldBlur"
                    :rules="[rules.required, rules.decimal, rules.fourDecimalPlacesOrFewer, rules.validSecondsForTextBox]"
                    type="number"
                    hide-spin-buttons
                  />
                </v-col>
                <v-col cols="auto">
                  <Icon
                    :icon="degreesEast ? 'mdi-alpha-e-box-outline' : 'mdi-alpha-w-box-outline'"
                    iconColor="primary"
                    large
                    :small="false"
                    :tooltipText="degreesEast ? $t('east') : $t('west')"
                    @icon-clicked="toggleEastWest"
                  />
                </v-col>
              </v-row>
            </DetailCard>
          </v-col>

          <v-col cols="12">
            <DetailCard
            :title="$t('shape')">
            <template #title.append>
              <v-btn
              v-if="showDeleteShape"
              @click="confirmDeleteOpened"
              icon
              small
              class="my-n2 mr-n2"
              color="error"
              id="delete-tract-shape">
                <v-icon>
                  mdi-delete-forever
                </v-icon>
              </v-btn>
            </template>
              <v-row dense>
                <v-col cols="12" sm="auto" v-if="showDownloadShape">
                  <v-btn
                  block
                  @click="uploadDownloadShapeClicked"
                  outlined
                  color="primary"
                  :id="uploadDownloadGeojsonId">
                    <v-row dense align="center" justify="center">
                      <v-col cols="auto">
                        <v-icon>
                          {{hasCutLayer ? 'mdi-download' : 'mdi-import'}}
                        </v-icon>
                      </v-col>
                      <v-col cols="auto">
                        <span>
                          {{ $t(hasCutLayer ? 'download' : 'import') }}
                        </span>
                      </v-col>
                    </v-row>
                  </v-btn>
                </v-col>

                <v-col cols="12" sm="">
                  <v-btn
                  block
                  @click="mutateGeojsonClicked"
                  elevation="0"
                  color="success"
                  :id="mutateGeojsonId">
                    <v-row dense justify="center" align="center">
                      <v-col cols="auto">
                        <v-icon>
                          {{ iconForMutateShape }}
                        </v-icon>
                      </v-col>
                      <v-col cols="auto">
                        <span>
                          {{ $t(textForMutateShape) }}
                        </span>
                      </v-col>
                    </v-row>
                  </v-btn>
                </v-col>
              </v-row>
            </DetailCard>
          </v-col>

          <v-col cols="12">
            <DetailCard :title="$t('permitNumbers')">
              <template #title.adjacent>
                <span v-if="permitNumbersHint" class="caption text--secondary ml-2">{{ permitNumbersHint }}</span>
              </template>
              <template #title.append>
                <Icon
                  icon="mdi-plus"
                  iconColor="primary"
                  :small="false"
                  :tooltipText="$t('addPermitNumber')"
                  elementId="add-permit-number"
                  @icon-clicked="addPermitNumber"
                />
              </template>

              <v-data-table
                dense
                :headers="permitNumberHeaders"
                :items="propTract.permitNumbers"
                hide-default-footer
                :items-per-page="-1"
                class="small-headers"
              >
                <template #item.number="{ item }">
                  <span>{{ item.number }}</span>
                </template>

                <template #item.expiresOn="{ item }">
                  <span>{{ formatDate(item.expiresOn) }}</span>
                </template>

                <template #item.actions="{ item }">
                  <Icon
                    icon="mdi-pencil"
                    iconColor="success"
                    small
                    :tooltipText="$t('edit')"
                    @icon-clicked="editPermitNumber(item)"
                  />
                  <Icon
                    icon="mdi-delete"
                    iconColor="error"
                    small
                    :tooltipText="$t('delete')"
                    @icon-clicked="deletePermitNumber(item)"
                  />
                </template>

                <template #no-data>
                  <span class="grey--text">{{ $t('noPermitNumbers') }}</span>
                </template>
              </v-data-table>
            </DetailCard>
          </v-col>
        </v-row>
      </v-col>

      <v-col cols="12" lg="7" xl="8" class="pb-0" id="tract-location-column">
        <div id="tract-location" :style="mapStyle">
          <div id="tract-location-map">
            <MapOverlays :map="map" autoShowSpecies ref="mapOverlays" @state="overlayState = $event" @toggle-species="toggleSpeciesLayer" />
            <v-row
            class="map-button-row-top"
            :class="{ 'flex-column align-start': layersVertical, 'map-button-row-top--passthrough': layersCollapsible }"
            dense
            :justify="layersCollapsible ? 'start' : 'end'">
              <div v-if="layersCollapsible" class="map-overlay-toggle-group">
                <Icon
                :icon="layersToggleIcon"
                @icon-clicked="layersOpen = !layersOpen"
                :tooltipText="$t('mapLayers')"
                xLarge
                :small="false"
                :iconColor="layersOpen ? 'primary' : 'grey darken-2'"
                margin="map-option-button"
                tooltipColor="black"
                elementId="tract-location-layers-toggle"
                />
              </div>
              <div
              v-show="!layersCollapsible || layersOpen"
              class="map-overlay-toggle-group"
              :class="{ 'map-overlay-toggle-group--vertical': layersVertical, 'ml-2': layersCollapsible && !layersVertical }"
              @mouseenter="hideMapTips"
              @click="layersOpen = false">
                <Icon
                v-if="$vuetify.breakpoint.lgAndUp && angelGeojson.layerIsNotEmpty(PARCELS)"
                icon="mdi-information-outline"
                @icon-clicked="parcelInfoToggled"
                :tooltipText="parcelInfoTooltip"
                xLarge
                :small="false"
                iconColor="white"
                margin="map-option-button"
                tooltipColor="black"
                elementId="tract-location-toggle-parcel-info"
                />
                <Icon
                icon="zoom-fit"
                @icon-clicked="locationMapWithLegend.zoom()"
                xLarge
                :small="false"
                iconColor="white"
                margin="map-option-button"
                tooltipColor="black"
                :tooltipText="$t('centerCamera')"
                elementId="tract-location-zoom-map"
                />
                <Icon
                v-if="overlayState.species.canShow"
                icon="mdi-paw"
                @icon-clicked="toggleSpeciesLayer"
                :disabled="otherOverlayActive('species')"
                :tooltipText="$t('speciesLayer')"
                xLarge
                :small="false"
                :iconColor="overlayState.species.active ? 'primary' : 'grey darken-2'"
                margin="map-option-button"
                tooltipColor="black"
                elementId="tract-species-layer-toggle"
                />
                <Icon
                v-if="companyInfo.hasCustomMapLayers"
                icon="mdi-layers-outline"
                @icon-clicked="toggleCustomLayers"
                :disabled="otherOverlayActive('custom')"
                :tooltipText="$t('customLayers')"
                xLarge
                :small="false"
                :iconColor="overlayState.custom.active || overlayState.custom.open ? 'primary' : 'grey darken-2'"
                margin="map-option-button"
                tooltipColor="black"
                elementId="tract-custom-layers-toggle"
                />
                <Icon
                icon="mdi-image-filter-hdr"
                @icon-clicked="toggleTopo"
                :disabled="otherOverlayActive('topo')"
                :tooltipText="$t('topoLayer')"
                xLarge
                :small="false"
                :iconColor="overlayState.topo.active ? 'primary' : 'grey darken-2'"
                margin="map-option-button"
                tooltipColor="black"
                elementId="tract-topo-toggle"
                />
                <Icon
                icon="mdi-tractor"
                @icon-clicked="analyzeSoil"
                :disabled="otherOverlayActive('soil')"
                :tooltipText="$t('analyzeOperability')"
                xLarge
                :small="false"
                :iconColor="overlayState.soil.active ? 'primary' : 'grey darken-2'"
                margin="map-option-button"
                tooltipColor="black"
                elementId="tract-soil-toggle"
                />
                <Icon
                icon="mdi-land-plots-marker"
                @icon-clicked="toggleNearbyParcels"
                :disabled="otherOverlayActive('nearbyParcels')"
                :tooltipText="$t(overlayState.nearbyParcels.active ? 'hideNearbyParcels' : 'showNearbyParcels')"
                xLarge
                :small="false"
                :iconColor="overlayState.nearbyParcels.active ? 'primary' : 'grey darken-2'"
                margin="map-option-button"
                tooltipColor="black"
                elementId="tract-nearby-parcels-toggle"
                />
                <Icon
                icon="mdi-crosshairs-gps"
                @icon-clicked="toggleNearbyTracts"
                :tooltipText="$t('toggleNearbyTracts', { toggle: $t(overlayState.nearbyTracts.active ? 'hide' : 'show') })"
                xLarge
                :small="false"
                :iconColor="overlayState.nearbyTracts.active ? 'primary' : 'grey darken-2'"
                margin="map-option-button"
                tooltipColor="black"
                elementId="tract-nearby-tracts-toggle"
                />
                <Icon
                v-if="overlayState.nearbyTracts.active"
                icon="mdi-vector-polygon"
                @icon-clicked="toggleNearbyBoundaries"
                :disabled="otherOverlayActive('nearbyBoundaries')"
                :tooltipText="$t('nearbyBoundaries')"
                xLarge
                :small="false"
                :iconColor="overlayState.nearbyBoundaries.active ? 'primary' : 'grey darken-2'"
                margin="map-option-button"
                tooltipColor="black"
                elementId="tract-nearby-boundaries-toggle"
                />
                <Icon
                icon="mdi-domain"
                @icon-clicked="toggleAccountLayer"
                :tooltipText="$t('accountLocations')"
                xLarge
                :small="false"
                :iconColor="showingAccountLayer ? 'primary' : 'grey darken-2'"
                margin="map-option-button"
                tooltipColor="black"
                elementId="tract-location-account-layer-toggle"
                />
              </div>
            </v-row>

            <div id="location-selection-map-top-left-container" :style="topLeftContainerStyle" v-if="viewingParcelInfo && showParcelInfoContainer" @mouseenter="hideMapTips">
              <v-row no-gutters>
                <v-col cols="12">
                  <v-card :color="SOFT_BLACK">
                    <v-card-title class="pt-3 px-3 pb-2">
                      <span class="body-2 white--text">{{ $t('parcel') }}</span>
                    </v-card-title>
                    <v-card-text class="px-3 pt-0 pb-3">
                      <v-row v-for="field in parcelInfo" :key="field.id" no-gutters justify="end" align="center" style="display: flex">
                        <v-col cols="">
                          <span class="caption white--text">{{ $t(field.label) }}:</span>
                        </v-col>
                        <v-col cols="auto" class="pl-2">
                          <span class="caption white--text text-right">{{ field.value }}</span>
                        </v-col>
                      </v-row>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>
            </div>

            <v-card v-if="legend" :style="legend.containerStyle" id="location-selection-map-legend" @mouseenter="hideMapTips">
              <v-col
              v-for="(legendItem) in legendContent"
              :key="legendItem.label"
              :style="{paddingTop: '6px', cursor: 'pointer'}"
              @click="toggleLegendItemVisibility(legendItem)"
              :id="legendItem.elementId">
                <v-row dense align="center">
                  <v-col cols="auto">
                    <v-icon
                    color="white"
                    small
                    v-if="legendItem.hidden">
                      mdi-eye-off
                    </v-icon>
                    <div
                    v-else
                    :style="legendItem.style">
                    </div>
                  </v-col>
                  <v-col cols="auto">
                    <span class="caption white--text">{{ $t(legendItem.label) }}</span>
                  </v-col>
                </v-row>
              </v-col>
            </v-card>

            <v-tooltip bottom color="black">
              <template
                v-slot:activator="{on}">
                <v-btn
                  class="scale-bar-toggle-button"
                  id="scale-bar-toggle"
                  v-on="on"
                  :ripple="false"
                  elevation="0"
                  color="#00000000"
                  @click="toggleScaleBar()">
                </v-btn>
              </template>
              <span class="subtitle-1 white--text" style="white-space: pre-line">{{ scaleBarToggleTooltip }}</span>
            </v-tooltip>
          </div>
        </div>
      </v-col>
    </v-row>

    <Dialog
    :stateId="dialogId"
    @close="dialogClosed"
    @click:outside="dialogClosed"
    @dialog-closing="dialogClosed">
      <PolygonImportDialog
      v-if="polygonImportOpen"
      :tract="propTract"
      @acres-calculated="onAcresCalculated"
      @geojson-import-initiated="onGeojsonImportInitialized">
      </PolygonImportDialog>

      <PolygonDownloadDialog
      v-else-if="polygonDownloadOpen"
      :tract="propTract"
      :angelGeojson="angelGeojson"
      @done="dialogClosed">
      </PolygonDownloadDialog>

      <ConfirmDelete
      v-else-if="confirmDeleteOpen"
      :title="$t('tractShape')"
      containerId="confirm-delete-tract-shape"
      @cancel-delete="dialogClosed"
      @delete="removeGeojsonClicked">
      </ConfirmDelete>

      <PermitNumberDialog
      v-else-if="permitNumberFormOpen"
      :permitNumber="selectedPermitNumber"
      @save="savePermitNumber"
      @cancel="dialogClosed">
      </PermitNumberDialog>
    </Dialog>
  </v-container>
</template>

<script>
import * as atlas from 'azure-maps-control'
import { accountsWithReceiverLocation, createAccountLocationMarker } from '@/utils/map/AccountMarkers.js'
import { milesBetween } from '@/utils/Distance.js'
import fieldRules from '@/utils/rules'
import counties from '@/utils/state-counties.json'
import { canonicalCountyName } from '@/utils/countyMatcher'
import { mapActions, mapGetters } from 'vuex'
import { LocalStorageKeys } from '@/utils/LocalStorageActor'
import { getFormattedCoordWithTrailingZeroes, getFormattedCoord, uuidv4 } from '@/utils/NumericMutations'
import { formatDate } from '@/utils/DateFormatter'
import { mapResizeTimeout } from '@/utils/map/MapHelpers'
import { overlaysStack } from '@/utils/map/overlayLayers.js'
import { angelGeojsonClient, AngelGeojson, AngelGeojsonLayer } from '@/utils/map/Geojson'
import { pointOutsideAllPolygons, getCoordinateOnFeature } from '@/utils/map/GeospatialHelpers.js'
import { geojson as geojsonSvg } from '@/images/AngelSvgLibrary.js'
import { uniqueDialogId } from '@/utils/componentHelpers.js'
import { getMapWithLegendInstance } from '@/utils/map/Map'

const TIME_MAXIMUM = 60
const DEGREE_LON_MAX = 180
const DEGREE_LAT_MAX = 90
const SECONDS_DECIMAL_LIMIT = 4
const CUT = AngelGeojsonLayer.Cut
const PARCELS = AngelGeojsonLayer.Parcels
const SOFT_BLACK = 'rgba(34, 34, 34, 0.867)'
const STANDARD_PADDING = 8
const px = (value) => `${value}px`

export default {
  name: 'LocationSelection',

  props: {
    spot: {
      type: Object,
      required: true
    },
    locations: {
      type: Array,
      default: () => []
    },
    propTract: {
      type: Object,
      required: false
    },
    isEditing: {
      type: Boolean,
      required: false,
      default: false
    },
    pickupRadiusDimensions: {
      type: [Number, undefined],
      required: false,
      default: undefined
    },
    angelGeojson: {
      type: AngelGeojson,
      required: true
    },
    notesHidden: {
      type: Boolean,
      required: false,
      default: true
    },
    permitNumbersHint: {
      type: String,
      default: ''
    }
  },

  components: {
    ConfirmDelete: () => import('../helper/ConfirmDelete.vue'),
    DetailCard: () => import('@/components/core/DetailCard.vue'),
    Dialog: () => import('@/components/Dialog.vue'),
    Icon: () => import('@/components/helper/Icon.vue'),
    PolygonImportDialog: () => import('./tract-form/PolygonImportDialog.vue'),
    PolygonDownloadDialog: () => import('./tract-form/PolygonDownloadDialog.vue'),
    PermitNumberDialog: () => import('./tract-form/PermitNumberDialog.vue'),
    MapOverlays: () => import('@/components/maps/MapOverlays.vue')
  },

  data: () => ({
    SOFT_BLACK,
    overlayState: { species: { canShow: false, active: false }, topo: { active: false }, soil: { active: false }, custom: { active: false, open: false }, nearbyTracts: { active: false }, nearbyBoundaries: { active: false }, nearbyParcels: { active: false } },
    PARCELS,
    geojsonSvg,

    dialogId: uniqueDialogId('location-selection'),

    locationMapWithLegend: null,
    marker: null,
    cutLayers: null,
    showMap: false,
    showingAccountLayer: false,
    accountLocations: [],
    accountMarkers: [],
    openAccountMarker: { marker: null, justAdded: false },

    dragging: false,
    mapStyle: {
      width: '100%',
      height: '60vh',
      borderRadius: '4px',
      overflow: 'hidden'
    },

    scaleBarRendered: false,
    currentScaleBar: 'imperial',
    scaleBarImport: undefined,
    scaleBarOptions: ['imperial', 'metric'],

    pickupRadiusLayers: undefined,
    cutSource: null,

    geojsonClientInitialized: false,
    angelGeojsonClient,
    polygonImportOpen: false,
    polygonDownloadOpen: false,
    confirmDeleteOpen: false,
    permitNumberFormOpen: false,
    selectedPermitNumber: null,

    viewingParcelInfo: true,
    layersOpen: false,

    latLon: true,
    rules: fieldRules.rules,

    latitude: null,
    longitude: null,
    latitudeTextField: null,
    longitudeTextField: null,

    degreesLat: null,
    minutesLat: null,
    secondsLat: null,
    degreesTextFieldLat: null,
    minutesTextFieldLat: null,
    secondsTextFieldLat: null,
    degreesLon: null,
    minutesLon: null,
    secondsLon: null,
    degreesTextFieldLon: null,
    minutesTextFieldLon: null,
    secondsTextFieldLon: null,
    degreesNorth: false,
    degreesNorthOriginal: false,
    degreesEast: false,
    degreesEastOriginal: false,

    countrySubdivision: '',
    countrySecondarySubdivision: ''
  }),

  computed: {
    ...mapGetters('dialog', ['topDialogState']),
    ...mapGetters('user', ['companyInfo']),
    map () {
      return this.locationMapWithLegend?.map
    },

    legend () {
      return this.locationMapWithLegend?.legend
    },

    pickupRadiusShape () {
      return this.locationMapWithLegend.pickupRadiusShape
    },

    selectedCounties () {
      if (counties[this.countrySubdivision] === undefined) return []
      const names = counties[this.countrySubdivision].map(c => c.N)
      // Account for counties in Virginia that also have a independent town associated with them.
      if (this.countrySecondarySubdivision === 'Franklin' || this.countrySecondarySubdivision === 'Bedford') names.push(this.countrySecondarySubdivision)
      return names
    },

    states () {
      return Object.keys(counties)
    },

    smallDevice () {
      return this.$vuetify.breakpoint.xsOnly
    },

    layersCollapsible () {
      return this.$vuetify.breakpoint.smAndDown
    },

    layersVertical () {
      return this.layersCollapsible && this.$vuetify.breakpoint.height >= this.$vuetify.breakpoint.width
    },

    layersToggleIcon () {
      if (this.layersVertical) return this.layersOpen ? 'mdi-chevron-up' : 'mdi-chevron-down'
      return this.layersOpen ? 'mdi-chevron-left' : 'mdi-chevron-right'
    },

    showApplyButton () {
      if (this.latLon) {
        return this.showApplyLatLon
      } else {
        return this.showApplyDMS
      }
    },

    showApplyLatLon () {
      const latitudeText = getFormattedCoord(this.latitudeTextField)
      const longitudeText = getFormattedCoord(this.longitudeTextField)
      const refsLoaded = this.$refs.latTField && this.$refs.lonTField
      const validFields = this.$refs.latTField?.valid && this.$refs.lonTField?.valid
      if (isNaN(latitudeText) || isNaN(longitudeText) || !this.rules.validLongitude(this.spot.longitude) || !this.rules.validLatitude(this.spot.latitude) || (!validFields && refsLoaded)) {
        return false
      }
      return parseFloat(this.latitude) !== latitudeText || parseFloat(this.longitude) !== longitudeText
    },

    showApplyDMS () {
      const degreeTextLat = getFormattedCoord(this.degreesTextFieldLat)
      const degreeTextLon = getFormattedCoord(this.degreesTextFieldLon)
      const minutesTextLat = getFormattedCoord(this.minutesTextFieldLat)
      const minutesTextLon = getFormattedCoord(this.minutesTextFieldLon)
      const secondsTextLat = parseFloat(this.secondsTextFieldLat).toFixed(SECONDS_DECIMAL_LIMIT)
      const secondsTextLon = parseFloat(this.secondsTextFieldLon).toFixed(SECONDS_DECIMAL_LIMIT)
      const refsLoaded = this.$refs.degreesLatTField && this.$refs.degreesLonTField && this.$refs.minutesLatTField && this.$refs.minutesLonTField && this.$refs.secondsLatTField && this.$refs.secondsLonTField
      const validFields = this.$refs.degreesLatTField?.valid && this.$refs.degreesLonTField?.valid && this.$refs.minutesLatTField?.valid && this.$refs.minutesLonTField?.valid && this.$refs.secondsLatTField?.valid && this.$refs.secondsLonTField?.valid

      if (isNaN(degreeTextLat) || isNaN(degreeTextLon) || isNaN(minutesTextLat) || isNaN(minutesTextLon) || isNaN(secondsTextLat) || isNaN(secondsTextLon) || !this.rules.validLongitude(this.spot.longitude) ||
        !this.rules.validLatitude(this.spot.latitude) || (!validFields && refsLoaded)) {
        return false
      }
      return parseFloat(this.degreesLat) !== degreeTextLat || parseFloat(this.degreesLon) !== degreeTextLon || parseFloat(this.minutesLat) !== minutesTextLat || parseFloat(this.minutesLon) !== minutesTextLon ||
        this.secondsLat.toFixed(SECONDS_DECIMAL_LIMIT) !== secondsTextLat || this.secondsLon.toFixed(SECONDS_DECIMAL_LIMIT) !== secondsTextLon || this.degreesNorth !== this.degreesNorthOriginal || this.degreesEast !== this.degreesEastOriginal
    },

    showResetButton () {
      if (this.latLon) {
        return this.showResetLatLon
      } else {
        return this.showResetDMS
      }
    },

    showResetLatLon () {
      const latitudeText = getFormattedCoord(this.latitudeTextField)
      const longitudeText = getFormattedCoord(this.longitudeTextField)
      return parseFloat(this.latitude) !== latitudeText || parseFloat(this.longitude) !== longitudeText
    },

    showResetDMS () {
      const degreeTextLat = getFormattedCoord(this.degreesTextFieldLat)
      const degreeTextLon = getFormattedCoord(this.degreesTextFieldLon)
      const minutesTextLat = getFormattedCoord(this.minutesTextFieldLat)
      const minutesTextLon = getFormattedCoord(this.minutesTextFieldLon)
      const secondsTextLat = parseFloat(this.secondsTextFieldLat).toFixed(SECONDS_DECIMAL_LIMIT)
      const secondsTextLon = parseFloat(this.secondsTextFieldLon).toFixed(SECONDS_DECIMAL_LIMIT)
      return parseFloat(this.degreesLat) !== degreeTextLat || parseFloat(this.degreesLon) !== degreeTextLon || parseFloat(this.minutesLat) !== minutesTextLat || parseFloat(this.minutesLon) !== minutesTextLon ||
        this.secondsLat.toFixed(SECONDS_DECIMAL_LIMIT) !== secondsTextLat || this.secondsLon.toFixed(SECONDS_DECIMAL_LIMIT) !== secondsTextLon || this.degreesNorth !== this.degreesNorthOriginal || this.degreesEast !== this.degreesEastOriginal
    },

    scaleBarToggleTooltip () {
      return this.$t('changeToUnits', { units: this.$t(this.currentScaleBar === 'imperial' ? 'metric' : 'imperial') })
    },

    geoLoading () {
      return this.angelGeojsonClient.loading
    },

    legendContent () {
      return this.legend?.content.filter(c => this.angelGeojson?.layerIsNotEmpty(PARCELS) || c.label !== 'parcel')
    },

    hasCutLayer () {
      return this.angelGeojson?.layerIsNotEmpty(CUT)
    },

    iconForMutateShape () {
      return this.hasCutLayer ? 'mdi-vector-square-edit' : 'mdi-vector-square-plus'
    },

    textForMutateShape () {
      return this.hasCutLayer ? 'edit' : 'create'
    },

    mutateGeojsonId () {
      return this.hasCutLayer ? 'edit-tract-geojson' : 'create-tract-geojson'
    },

    uploadDownloadGeojsonId () {
      return this.hasCutLayer ? 'download-tract-geojson' : 'import-tract-geojson'
    },

    lessVerboseUi () {
      return this.$vuetify.breakpoint.xs || (this.$vuetify.breakpoint.lg && !this.notesHidden)
    },

    showDownloadShape () {
      return !!this.propTract.tractId || !this.hasCutLayer
    },

    showDeleteShape () {
      return this.locationMapWithLegend?.angelGeojson?.layerIsNotEmpty(CUT) ?? false
    },

    showParcelInfoContainer () { return this.viewingParcelInfo && this.angelGeojson.layerIsNotEmpty(PARCELS) && this.$vuetify.breakpoint.lgAndUp },

    topLeftContainerStyle () {
      const showTopLeftContainer = this.viewingParcelInfo || this.showCutInfoContainer
      const opacity = showTopLeftContainer ? '1' : '0'
      const zIndex = showTopLeftContainer ? 1 : -1
      return { position: 'absolute', top: px(STANDARD_PADDING), left: px(STANDARD_PADDING), zIndex, transition: '.1s', overflow: 'hidden', maxWidth: 'max(200px, 20%)', opacity }
    },

    parcelInfo () {
      const parcelFieldsForDisplay = [{
        label: 'parcelNumber',
        parcelKey: 'parcel_id'
      }, {
        label: 'owner',
        parcelKey: 'owner'
      }]

      const parcelFeature = this.angelGeojson?.getFeatures(PARCELS)[0]
      if (!parcelFeature) return undefined
      const props = parcelFeature?.properties?.fields
      const result = parcelFieldsForDisplay
        .filter(field => props[field.parcelKey] ?? false)
        .map(field => ({
          ...field,
          value: props[field.parcelKey]
        }))
      if (result.length < 1) {
        return [{
          label: 'parcelNumber',
          value: this.$t('unknown')
        }]
      }
      return result
    },

    parcelInfoTooltip () {
      return this.viewingParcelInfo ? this.$t('hideParcelInformation') : this.$t('showParcelInformation')
    },

    permitNumberHeaders () {
      return [
        { text: this.$t('permitNumber'), value: 'number', sortable: false },
        { text: this.$t('expiresOn'), value: 'expiresOn', sortable: false },
        { text: this.$t('actions'), value: 'actions', sortable: false, align: 'center' }
      ]
    }
  },

  watch: {
    spot: {
      async handler (spot) {
        const outOfCutLayerBounds = this.spotOutsideCutBoundary(spot)
        if (!outOfCutLayerBounds) {
          this.updatePosition(spot)
          const outOfParcelBounds = this.spotOutsideParcelBoundary(spot)
          if (outOfParcelBounds) this.$emit('out-of-parcel-bounds')
        } else {
          this.movePositionInsideBoundary(spot)
        }
      },
      immediate: true
    },

    pickupRadiusDimensions (radius) {
      this.locationMapWithLegend.setPickupRadius(radius)
    },

    angelGeojson: {
      handler (geojson) {
        if (this.showMap) this.locationMapWithLegend.setAngelGeojson(geojson)
      },
      deep: true
    },

    '$vuetify.breakpoint.height' () { this.onResize() },
    '$vuetify.breakpoint.width' () { this.onResize() },
    'topDialogState.fullscreen' () { this.onResize() }
  },

  mounted () {
    this.spotOriginal = JSON.parse(JSON.stringify(this.spot))
    this.$nextTick(async _ => {
      await this.initMap()
    })
  },

  beforeDestroy () {
    if (this.mapResizeObserver) this.mapResizeObserver.disconnect()
    this.mapResizeObserver = null
    this.locationMapWithLegend.dispose()
    this.locationMapWithLegend = null
  },

  methods: {
    ...mapActions('tract', ['getLocationWithCoordinate', 'getLocationWithCompanyAddress']),
    ...mapActions('dialog', ['openOrUpdateDialog', 'closeDialogsAtOrAbove']),
    ...mapActions('account', ['fetchAccounts']),

    async toggleAccountLayer () {
      this.showingAccountLayer = !this.showingAccountLayer
      if (this.showingAccountLayer && this.accountLocations.length === 0) {
        this.accountLocations = accountsWithReceiverLocation(await this.fetchAccounts())
      }
      this.renderAccountMarkers()
    },

    renderAccountMarkers () {
      this.accountMarkers.forEach(({ marker, clickHandler }) => {
        marker.options.popup.remove()
        this.map.events.remove('click', marker, clickHandler)
        this.map.markers.remove(marker)
      })
      this.accountMarkers = []
      this.openAccountMarker = { marker: null, justAdded: false }
      if (!this.showingAccountLayer || !this.map) return
      const origin = { latitude: this.latitude, longitude: this.longitude }
      this.accountMarkers = this.accountLocations.map(account => {
        const miles = milesBetween(origin, { latitude: account.receiverSpotLatitude, longitude: account.receiverSpotLongitude })
        const marker = createAccountLocationMarker(
          account,
          account.isExternal ? this.$t('external') : this.$t('internal'),
          miles !== undefined ? this.$t('milesShort', { distance: miles.toFixed(1) }) : undefined)
        const clickHandler = () => {
          if (this.openAccountMarker.marker?.options.popup) {
            this.openAccountMarker.marker.options.popup.remove()
          }
          this.openAccountMarker = { marker, justAdded: true }
          marker.togglePopup()
        }
        return { marker, clickHandler }
      })
      this.accountMarkers.forEach(({ marker, clickHandler }) => {
        this.map.events.add('click', marker, clickHandler)
        this.map.markers.add(marker)
      })
    },

    toggleSpeciesLayer () {
      if (this.otherOverlayActive('species')) {
        this.setSnack(this.$t('disableOtherLayersToToggle', { layer: this.$t('speciesLayer') }))
        return
      }
      if (this.$refs.mapOverlays) this.$refs.mapOverlays.toggleSpeciesLayer()
    },

    toggleTopo () {
      if (this.$refs.mapOverlays) this.$refs.mapOverlays.toggleTopo()
    },

    toggleCustomLayers () {
      if (this.$refs.mapOverlays) this.$refs.mapOverlays.toggleCustomLayersPanel()
    },

    analyzeSoil () {
      if (this.$refs.mapOverlays) this.$refs.mapOverlays.analyzeSoilOperability()
    },

    toggleNearbyTracts () {
      if (this.$refs.mapOverlays) this.$refs.mapOverlays.toggleNearbyTracts({ tractId: this.propTract?.tractId })
    },

    toggleNearbyBoundaries () {
      if (this.$refs.mapOverlays) this.$refs.mapOverlays.toggleNearbyTractBoundaries({ tractId: this.propTract?.tractId, spot: this.spot })
    },

    toggleNearbyParcels () {
      if (this.$refs.mapOverlays) this.$refs.mapOverlays.toggleNearbyParcels()
    },

    hideMapTips () {
      if (this.$refs.mapOverlays) this.$refs.mapOverlays.hideDataTips()
    },

    otherOverlayActive (name) {
      return ['species', 'topo', 'soil', 'custom', 'nearbyBoundaries', 'nearbyParcels'].some(o => o !== name && !overlaysStack(name, o) && this.overlayState[o]?.active)
    },

    updatePosition (spot) {
      this.latitude = spot.latitude
      this.longitude = spot.longitude
      this.latitudeTextField = getFormattedCoordWithTrailingZeroes(spot.latitude)
      this.longitudeTextField = getFormattedCoordWithTrailingZeroes(spot.longitude)

      this.latLonValuestoDMS()

      this.countrySubdivision = spot.countrySubdivision
      this.countrySecondarySubdivision = spot.countrySecondarySubdivision

      if (this.marker && this.map) {
        this.marker.setOptions({
          position: [spot.longitude, spot.latitude]
        })
      }

      this.renderAccountMarkers()
    },

    movePositionInsideBoundary () {
      const originalSpotIsValid = this.spotOriginal && !this.spotOutsideCutBoundary(this.spotOriginal)
      if (originalSpotIsValid) {
        const { longitude, latitude } = this.spotOriginal
        this.latitude = latitude
        this.longitude = longitude
        this.handleChangedCoordinate({ longitude, latitude })
      } else {
        const { longitude, latitude } = getCoordinateOnFeature(this.angelGeojson.getFirstFeature(CUT))
        this.latitude = latitude
        this.longitude = longitude
        this.handleChangedCoordinate({ longitude, latitude })
      }

      this.setSnackError(this.$t('cannotMoveTractOutsideCut'))
    },

    onGeojsonImportInitialized (geojsonImportModule) {
      this.$emit('geojson-import-initialized', geojsonImportModule)
      this.dialogClosed()
    },

    latLonValuestoDMS () {
      const latConversion = this.convertToDMS(this.latitude)
      this.degreesLat = latConversion.degrees
      this.minutesLat = latConversion.minutes
      this.secondsLat = latConversion.seconds

      this.degreesNorth = (this.latitude >= 0)
      this.degreesNorthOriginal = (this.latitude >= 0)

      this.degreesTextFieldLat = parseInt(this.degreesLat)
      this.minutesTextFieldLat = parseInt(this.minutesLat)
      this.secondsTextFieldLat = this.secondsLat.toFixed(SECONDS_DECIMAL_LIMIT)

      const lonConversion = this.convertToDMS(this.longitude)
      this.degreesLon = lonConversion.degrees
      this.minutesLon = lonConversion.minutes
      this.secondsLon = lonConversion.seconds

      this.degreesEast = (this.longitude >= 0)
      this.degreesEastOriginal = (this.longitude >= 0)

      this.degreesTextFieldLon = parseInt(this.degreesLon)
      this.minutesTextFieldLon = parseInt(this.minutesLon)
      this.secondsTextFieldLon = this.secondsLon.toFixed(SECONDS_DECIMAL_LIMIT)
    },

    convertToDMS (value) {
      const wholeVal = Math.abs(value)
      const degrees = Math.floor(wholeVal)
      const minutesNotFixed = (wholeVal - degrees) * 60
      const minutes = Math.floor(minutesNotFixed)
      const seconds = (minutesNotFixed - minutes) * 60

      return {
        degrees: degrees,
        minutes: minutes,
        seconds: seconds
      }
    },

    convertToLatLon (val) {
      const degrees = val.degrees
      const minutes = val.minutes
      const seconds = val.seconds
      const postive = val.northEast

      return postive ? degrees + (minutes / 60) + (seconds / 3600) : -1 * (degrees + (minutes / 60) + (seconds / 3600))
    },

    toggleInputType () {
      this.latLon = !this.latLon
    },

    toggleNorthSouth () {
      this.degreesNorth = !this.degreesNorth
    },

    toggleEastWest () {
      this.degreesEast = !this.degreesEast
    },

    applyLocation () {
      if (this.latLon && this.rules.validLongitude(this.longitude) && this.rules.validLatitude(this.latitude)) {
        const coordinate = {
          latitude: getFormattedCoord(this.latitudeTextField),
          longitude: getFormattedCoord(this.longitudeTextField)
        }
        this.handleChangedCoordinate(coordinate)
      } else if (!this.latLon) {
        const DMSObjectLat = {
          degrees: parseFloat(this.degreesTextFieldLat),
          minutes: parseFloat(this.minutesTextFieldLat),
          seconds: parseFloat(this.secondsTextFieldLat),
          northEast: this.degreesNorth
        }
        const DMSObjectLon = {
          degrees: parseFloat(this.degreesTextFieldLon),
          minutes: parseFloat(this.minutesTextFieldLon),
          seconds: parseFloat(this.secondsTextFieldLon),
          northEast: this.degreesEast
        }
        const coordinate = {
          latitude: getFormattedCoord(this.convertToLatLon(DMSObjectLat)),
          longitude: getFormattedCoord(this.convertToLatLon(DMSObjectLon))
        }
        this.handleChangedCoordinate(coordinate)
      }
    },

    resetLocation () {
      this.degreesNorth = this.degreesNorthOriginal
      this.degreesEast = this.degreesEastOriginal
      this.$emit('location-changed', {
        latitude: parseFloat(this.latitude.toFixed(6)),
        longitude: parseFloat(this.longitude.toFixed(6)),
        countrySubdivision: this.countrySubdivision,
        countrySecondarySubdivision: this.countrySecondarySubdivision
      })
    },

    initMap () {
      this.locationMapWithLegend = getMapWithLegendInstance({
        angelGeojson: this.angelGeojson,
        onReady: () => {
          this.addMarker()
          this.map.events.add('click', () => {
            if (this.openAccountMarker.marker !== null && this.openAccountMarker.justAdded === false && this.openAccountMarker.marker.options.popup) {
              this.openAccountMarker.marker.options.popup.remove()
              this.openAccountMarker.marker = null
            }
            this.openAccountMarker.justAdded = false
          })
          this.showMap = true
        },
        mapHtmlId: 'tract-location-map',
        pickupRadius: this.pickupRadiusDimensions,
        latitude: this.latitude,
        longitude: this.longitude,
        zoom: 14
      })

      if (!this.scaleBarRendered && document.getElementById('atlasMapControlImport')) {
        this.scaleBarRendered = true
        import('@/utils/azure-maps-scale-bar-control.js').then(
          async sb => {
            this.scaleBarImport = sb
            await this.toggleScaleBar(true)
          })
      }
      this.onResize()
      this.observeMapContainer()
    },

    observeMapContainer () {
      const el = document.getElementById('tract-location')
      if (!el) return
      this.mapResizeObserver = new ResizeObserver(() => {
        if (this.map) this.map.resize()
      })
      this.mapResizeObserver.observe(el)
    },

    countyChosen () {
      const index = counties[this.countrySubdivision].findIndex(c => c.N === this.countrySecondarySubdivision)
      const county = counties[this.countrySubdivision][index]
      const coords = county.C.split(',')
      const coordinate = {
        latitude: parseFloat(coords[0]),
        longitude: parseFloat(coords[1])
      }
      this.locationMapWithLegend.setCoords(coordinate.latitude, coordinate.longitude)
      this.emitLocationAndDragCamera(coordinate, this.countrySubdivision, county.N)
    },

    stateChanged () {
      this.countrySecondarySubdivision = this.selectedCounties[0]
      this.countyChosen()
    },

    addMarker () {
      const position = [this.longitude, this.latitude]

      this.marker = new atlas.HtmlMarker({
        draggable: true,
        position: position,
        pixelOffset: [0, 0],
        color: '#D15F27'
      })

      this.setMarkerHtmlId()

      this.map.events.add('dragend', this.marker, (e) => {
        const coordinate = (e.target.marker._lngLat)
        const coordinateFormatted = { latitude: coordinate?.lat, longitude: coordinate?.lng }
        this.handleChangedCoordinate(coordinateFormatted)
      })

      this.map.markers.add(this.marker)
    },

    setMarkerHtmlId () {
      const marker = this.marker
      const options = marker.getOptions()
      const parser = new DOMParser()
      const parsedDocument = parser.parseFromString(options.htmlContent, 'text/html')
      const element = parsedDocument.body.firstChild
      element.setAttribute('id', 'tract-location-marker')
      const serializer = new XMLSerializer()
      const elementAsString = serializer.serializeToString(element)
      this.marker.setOptions({ ...options, htmlContent: elementAsString })
    },

    async handleChangedCoordinate (coordinate) {
      if (this.locationMapWithLegend) this.locationMapWithLegend.setCoords(coordinate.latitude, coordinate.longitude)
      await this.getCountyForCoordinate(coordinate)
    },

    async getCountyForCoordinate (coordinate) {
      try {
        const { countrySubdivision, countrySecondarySubdivision } = await this.getLocationWithCoordinate({
          latitude: coordinate.latitude,
          longitude: coordinate.longitude
        })
        // Reconcile the geocoder's county spelling with our canonical list so we never
        // store a value the County dropdown can't produce (e.g. "Saint Clair" vs "St. Clair").
        const canonicalCounty = canonicalCountyName(countrySubdivision, countrySecondarySubdivision)
        this.countrySubdivision = countrySubdivision
        if (canonicalCounty) {
          this.countrySecondarySubdivision = canonicalCounty
          this.emitLocationAndDragCamera(coordinate, countrySubdivision, canonicalCounty)
        } else {
          // County couldn't be matched. Apply the coordinate and state, but leave the county
          // blank for manual selection rather than persisting a non-canonical name.
          this.countrySecondarySubdivision = ''
          this.emitLocationAndDragCamera(coordinate, countrySubdivision, '')
          this.setSnackError(this.$t('countyNotRecognizedSelectManually'))
        }
      } catch (error) {
        this.setSnackError(this.$t('errorGettingLocationStateAndCounty'))
      }
    },

    emitLocationAndDragCamera (coordinate, state, county) {
      this.emitLocation(coordinate, state, county)
      this.setPickupRadiusCoordinates(coordinate)
      this.angelGeojson.layerIsNotEmpty(CUT)
        ? this.locationMapWithLegend.zoom()
        : this.locationMapWithLegend.zoomCoordinate({ type: 'ease', duration: 350 })
    },

    emitLocation (coordinate, state, county) {
      this.$emit('location-changed', {
        latitude: parseFloat(coordinate.latitude.toFixed(6)),
        longitude: parseFloat(coordinate.longitude.toFixed(6)),
        countrySubdivision: state,
        countrySecondarySubdivision: county
      })
    },

    setPickupRadiusCoordinates (coordinate) {
      if (this.pickupRadiusShape !== undefined) {
        const p = atlas.data.Position.fromLatLng([coordinate.longitude, coordinate.latitude])
        this.pickupRadiusShape.setCoordinates(p)
      }
    },

    async toggleScaleBar (firstRender = false) {
      if (firstRender) {
        this.currentScaleBar = this.scaleBarOptions[parseInt(localStorage.getItem(LocalStorageKeys.TRACT_MAP_SCALE_BAR_TYPE))] ?? this.currentScaleBar
      } else {
        const options = this.scaleBarOptions
        this.currentScaleBar = options[(options.indexOf(this.currentScaleBar) + 1) % options.length]
        localStorage.setItem(LocalStorageKeys.TRACT_MAP_SCALE_BAR_TYPE, options.indexOf(this.currentScaleBar))
        if (this.scaleBarControl) {
          this.map.controls.remove(this.scaleBarControl)
          this.scaleBarControl = undefined
        }
      }
      if (this.currentScaleBar) {
        this.scaleBarControl = new this.scaleBarImport.atlas.control.ScaleBarControl({ maxBarLength: 120, units: this.currentScaleBar })
        this.map.controls.add(this.scaleBarControl)
      }
    },

    onResize () {
      const tractFormHeight = document.getElementById('tract-form')?.offsetHeight
      this.mapStyle = {
        width: '100%',
        height: tractFormHeight ? `${tractFormHeight - 210}px` : '42vh',
        maxHeight: '75vw',
        borderRadius: '4px',
        overflow: 'hidden'
      }
      if (this.map) {
        setTimeout(() => this.map.resize(), mapResizeTimeout)
      }
    },

    onTextFieldBlur () {
      if (this.latLon && (this.rules.validLatitudeForTextBox(this.latitudeTextField) || Math.abs(parseFloat(this.latitudeTextField)) <= DEGREE_LAT_MAX)) {
        this.latitudeTextField = getFormattedCoordWithTrailingZeroes(this.latitudeTextField)
      }
      if (this.latLon && (this.rules.validLongitudeForTextBox(this.longitudeTextField) || Math.abs(parseFloat(this.longitudeTextField)) <= DEGREE_LON_MAX)) {
        this.longitudeTextField = getFormattedCoordWithTrailingZeroes(this.longitudeTextField)
      }
      if (!this.latLon && (this.rules.validDegreesNorthSouthForTextBox(this.degreesTextFieldLat) || (Math.abs(parseFloat(this.degreesTextFieldLat)) <= DEGREE_LAT_MAX && parseFloat(this.degreesTextFieldLat) >= 0))) {
        this.degreesTextFieldLat = parseInt(Math.floor(this.degreesTextFieldLat))
      }
      if (!this.latLon && (this.rules.validDegreesEastWestForTextBox(this.degreesTextFieldLon) || (Math.abs(parseFloat(this.degreesTextFieldLon)) <= DEGREE_LON_MAX && parseFloat(this.degreesTextFieldLon) >= 0))) {
        this.degreesTextFieldLon = parseInt(Math.floor(this.degreesTextFieldLon))
      }
      if (!this.latLon && (this.rules.validMinutesForTextBox(this.minutesTextFieldLat) || (Math.abs(parseFloat(this.minutesTextFieldLat)) <= TIME_MAXIMUM && parseFloat(this.minutesTextFieldLat) >= 0))) {
        this.minutesTextFieldLat = parseInt(Math.floor(this.minutesTextFieldLat))
      }
      if (!this.latLon && (this.rules.validMinutesForTextBox(this.minutesTextFieldLon) || (Math.abs(parseFloat(this.minutesTextFieldLon)) <= TIME_MAXIMUM && parseFloat(this.minutesTextFieldLon) >= 0))) {
        this.minutesTextFieldLon = parseInt(Math.floor(this.minutesTextFieldLon))
      }
      if (!this.latLon && (this.rules.validSecondsForTextBox(this.secondsTextFieldLat) || (Math.abs(parseFloat(this.secondsTextFieldLat)) <= TIME_MAXIMUM && parseFloat(this.secondsTextFieldLat) >= 0))) {
        this.secondsTextFieldLat = parseFloat(this.secondsTextFieldLat).toFixed(SECONDS_DECIMAL_LIMIT)
      }
      if (!this.latLon && (this.rules.validSecondsForTextBox(this.secondsTextFieldLon) || (Math.abs(parseFloat(this.secondsTextFieldLon)) <= TIME_MAXIMUM && parseFloat(this.secondsTextFieldLon) >= 0))) {
        this.secondsTextFieldLon = parseFloat(this.secondsTextFieldLon).toFixed(SECONDS_DECIMAL_LIMIT)
      }
    },

    mutateGeojsonClicked () {
      this.$emit('mutate-geojson-clicked')
    },

    removeGeojsonClicked () {
      this.locationMapWithLegend.removeLayer(CUT)
      this.$emit('geojson-cut-layer-removed')
      this.dialogClosed()
    },

    spotOutsideCutBoundary (spot) {
      if (!this.angelGeojson?.getFeatures(CUT)) return false
      return pointOutsideAllPolygons(spot, this.angelGeojson.getFeatures(CUT))
    },

    spotOutsideParcelBoundary (spot) {
      if (!this.angelGeojson?.getFeatures(PARCELS)) return false
      return pointOutsideAllPolygons(spot, this.angelGeojson.getFeatures(PARCELS))
    },

    toggleLegendItemVisibility (legendItem) {
      this.locationMapWithLegend.toggleLegendItemVisibility(legendItem)
    },

    dialogClosed () {
      this.polygonImportOpen = false
      this.polygonDownloadOpen = false
      this.confirmDeleteOpen = false
      this.permitNumberFormOpen = false
      this.selectedPermitNumber = null
      this.closeDialogsAtOrAbove(this.dialogId)
    },

    importDialogOpened () {
      this.polygonImportOpen = true
      this.openOrUpdateDialog({ id: this.dialogId, width: '600px' })
    },

    downloadDialogOpened () {
      this.polygonDownloadOpen = true
      this.openOrUpdateDialog({ id: this.dialogId, width: '400px' })
    },

    confirmDeleteOpened () {
      this.confirmDeleteOpen = true
      this.openOrUpdateDialog({ id: this.dialogId, width: '400px' })
    },

    uploadDownloadShapeClicked () {
      if (this.hasCutLayer) {
        this.downloadDialogOpened()
      } else {
        this.importDialogOpened()
      }
    },

    onAcresCalculated (acres) {
      this.$emit('acres-calculated', acres)
    },

    parcelInfoToggled () {
      this.viewingParcelInfo = !this.viewingParcelInfo
    },

    formatDate,

    addPermitNumber () {
      this.selectedPermitNumber = {
        number: '',
        expiresOn: null,
        uuid: uuidv4()
      }
      this.permitNumberFormOpen = true
      this.openOrUpdateDialog({ id: this.dialogId, width: '500px' })
    },

    editPermitNumber (permitNumber) {
      this.selectedPermitNumber = permitNumber
      this.permitNumberFormOpen = true
      this.openOrUpdateDialog({ id: this.dialogId, width: '500px' })
    },

    savePermitNumber (permitNumber) {
      const permitNumbers = this.propTract.permitNumbers || []
      const index = permitNumbers.findIndex(f => (f.permitNumberId && f.permitNumberId === permitNumber.permitNumberId) || (f.uuid && f.uuid === permitNumber.uuid && !permitNumber.permitNumberId))

      if (index >= 0) {
        permitNumbers.splice(index, 1, permitNumber)
      } else {
        permitNumbers.push(permitNumber)
      }

      this.propTract.permitNumbers = permitNumbers

      this.$emit('permit-numbers-updated', permitNumbers)
      this.dialogClosed()
    },

    deletePermitNumber (permitNumber) {
      const permitNumbers = this.propTract.permitNumbers || []
      const index = permitNumbers.findIndex(f => (f.permitNumberId && f.permitNumberId === permitNumber.permitNumberId) || (f.uuid && f.uuid === permitNumber.uuid && !permitNumber.permitNumberId))

      if (index >= 0) {
        permitNumbers.splice(index, 1)
      }

      this.propTract.permitNumbers = permitNumbers

      this.$emit('permit-numbers-updated', permitNumbers)
    },

    dateSelected (date) {
      this.selectedPermitNumber.expiresOn = date
    }
  }
}
</script>

<style scoped src="@/styles/map-scoped.css"/>
<style src="@/styles/map-global.css"/>
